import {
  signInWithEmailAndPassword as firebaseSignIn,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut as firebaseSignOut,
  onAuthStateChanged as firebaseOnAuthStateChanged,
  sendPasswordResetEmail as firebaseSendPasswordResetEmail,
  updatePassword,
  deleteUser,
  User as FirebaseUser
} from 'firebase/auth';
import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  deleteDoc,
  collection,
  query,
  where,
  getDocs,
  addDoc,
  serverTimestamp,
  Timestamp,
  runTransaction,
  arrayUnion
} from 'firebase/firestore';
import { auth, db } from '@/services/firebase/config';
import {
  UserAuth,
  TeacherProfile,
  AuthCredentials,
  SignUpData,
  QuestionSet,
  QuestionFolder,
  GameSession,
  SessionCreationData,
  SessionJoinData,
  SessionControlAction,
  SessionStats,
  Question,
  SessionStage
} from '@/types';
import type { Player, GrenadeThrowEvent } from '@/types';
import { DEFAULT_TIMER_CONFIG } from '@/config/timerConfig';
import logger from '@/utils/logger';
import { AuthService } from './AuthService';
import { transformQuestionSetToGameData } from '@/utils/questionSetUtils';
import { getArenaPlayerStartPosition } from '@/constants/arena';

const DEFAULT_BONUS_DURATION = 8;

export class FirebaseAuthService implements AuthService {
  private googleProvider: GoogleAuthProvider;

  constructor() {
    this.googleProvider = new GoogleAuthProvider();
    this.googleProvider.setCustomParameters({
      prompt: 'select_account'
    });
  }

  // Convert Firebase User to our UserAuth interface
  private mapFirebaseUserToUserAuth(firebaseUser: FirebaseUser): UserAuth {
    return {
      uid: firebaseUser.uid,
      email: firebaseUser.email || '',
      role: 'teacher', // Default to teacher for teacher dashboard
      displayName: firebaseUser.displayName || '',
      photoURL: firebaseUser.photoURL || undefined,
      emailVerified: firebaseUser.emailVerified,
      createdAt: firebaseUser.metadata.creationTime ? new Date(firebaseUser.metadata.creationTime) : undefined,
      lastLoginAt: firebaseUser.metadata.lastSignInTime ? new Date(firebaseUser.metadata.lastSignInTime) : undefined
    };
  }

  // Convert Firestore Timestamp to Date
  private convertTimestamp(timestamp: Timestamp | Date | string | { seconds: number } | null | undefined): Date {
    if (timestamp === null || timestamp === undefined) {
      return new Date();
    }
    if (timestamp instanceof Timestamp) {
      return timestamp.toDate();
    }
    if (timestamp && typeof timestamp === 'object' && 'seconds' in timestamp) {
      return new Date(timestamp.seconds * 1000);
    }
    if (timestamp instanceof Date) {
      return timestamp;
    }
    // Handle ISO date strings (from JSON serialization)
    if (typeof timestamp === 'string') {
      const parsed = new Date(timestamp);
      // Verify it's a valid date
      if (!isNaN(parsed.getTime())) {
        return parsed;
      }
    }
    // Fallback: return current date (should rarely happen)
    logger.debug('convertTimestamp fell back to current time', { timestamp });
    return new Date();
  }

  private getStage(session: GameSession, index: number = session.currentStageIndex): SessionStage | null {
    return session.stages[index] ?? null;
  }

  private getQuestionDurations(question?: Question) {
    return {
      reading: question?.questionReadingTimeSeconds ?? DEFAULT_TIMER_CONFIG.questionPhaseSeconds,
      running: question?.arenaPlayTimeSeconds ?? DEFAULT_TIMER_CONFIG.runningPhaseSeconds,
    };
  }

  private mapQuestionSetSnapshot(
    snapshot: unknown,
    fallback: {
      questionSetId: string;
      questionSetName: string;
      teacherId: string;
      createdAt?: Timestamp | Date | string | { seconds: number } | null;
      updatedAt?: Timestamp | Date | string | { seconds: number } | null;
    }
  ): QuestionSet | undefined {
    if (!snapshot || typeof snapshot !== 'object') {
      return undefined;
    }

    const rawSnapshot = snapshot as Partial<QuestionSet> & {
      createdAt?: Timestamp | Date | string | { seconds: number } | null;
      updatedAt?: Timestamp | Date | string | { seconds: number } | null;
    };

    const questions = Array.isArray(rawSnapshot.questions) ? rawSnapshot.questions : [];
    const difficulty = rawSnapshot.difficulty;
    const status = rawSnapshot.status;
    const tags = Array.isArray(rawSnapshot.tags)
      ? rawSnapshot.tags.filter((tag): tag is string => typeof tag === 'string')
      : undefined;

    return {
      id: typeof rawSnapshot.id === 'string' ? rawSnapshot.id : fallback.questionSetId,
      name: typeof rawSnapshot.name === 'string' ? rawSnapshot.name : fallback.questionSetName,
      description: typeof rawSnapshot.description === 'string' ? rawSnapshot.description : undefined,
      questions,
      createdBy: typeof rawSnapshot.createdBy === 'string' ? rawSnapshot.createdBy : fallback.teacherId,
      createdAt: this.convertTimestamp(rawSnapshot.createdAt ?? fallback.createdAt ?? new Date()),
      updatedAt: this.convertTimestamp(rawSnapshot.updatedAt ?? fallback.updatedAt ?? new Date()),
      isActive: typeof rawSnapshot.isActive === 'boolean' ? rawSnapshot.isActive : true,
      totalQuestions: typeof rawSnapshot.totalQuestions === 'number' ? rawSnapshot.totalQuestions : questions.length,
      estimatedDuration: typeof rawSnapshot.estimatedDuration === 'number' ? rawSnapshot.estimatedDuration : 0,
      difficulty:
        difficulty === 'easy' || difficulty === 'medium' || difficulty === 'hard' || difficulty === 'mixed'
          ? difficulty
          : 'mixed',
      tags,
      status: status === 'draft' || status === 'published' ? status : 'published',
      folderId: typeof rawSnapshot.folderId === 'string' ? rawSnapshot.folderId : undefined
    };
  }

  private cleanUndefined(obj: Record<string, unknown>): Record<string, unknown> {
    return Object.entries(obj).reduce<Record<string, unknown>>((acc, [key, value]) => {
      if (value !== undefined) {
        acc[key] = value;
      }
      return acc;
    }, {});
  }

  private deepCleanUndefined(obj: unknown): unknown {
    if (obj === null || obj === undefined) {
      return obj;
    }

    if (typeof obj !== 'object') {
      return obj;
    }

    if (Array.isArray(obj)) {
      return obj.map(item => this.deepCleanUndefined(item)).filter(item => item !== undefined);
    }

    const constructorName = (obj as { constructor?: { name?: string } }).constructor?.name;
    if (constructorName && constructorName !== 'Object') {
      return obj;
    }

    const plainObject = obj as Record<string, unknown>;
    const cleaned: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(plainObject)) {
      if (value !== undefined) {
        const cleanedValue = this.deepCleanUndefined(value);
        if (cleanedValue !== undefined) {
          cleaned[key] = cleanedValue;
        }
      }
    }

    return cleaned;
  }

  private buildGameSessionUpdatePayload(updates: Partial<GameSession>): Record<string, unknown> {
    return this.deepCleanUndefined({
      ...updates,
      startedAt: updates.startedAt instanceof Date ? serverTimestamp() : updates.startedAt,
      completedAt: updates.completedAt instanceof Date ? serverTimestamp() : updates.completedAt,
      currentQuestionStartTime: updates.currentQuestionStartTime instanceof Date ? serverTimestamp() : updates.currentQuestionStartTime,
      currentPhaseStartTime: updates.currentPhaseStartTime instanceof Date
        ? Timestamp.fromDate(updates.currentPhaseStartTime)
        : updates.currentPhaseStartTime,
      currentPhaseEndTime: updates.currentPhaseEndTime instanceof Date
        ? Timestamp.fromDate(updates.currentPhaseEndTime)
        : updates.currentPhaseEndTime,
      updatedAt: serverTimestamp()
    }) as Record<string, unknown>;
  }

  private async updateQuestionStageSession(sessionId: string, updates: Partial<GameSession>): Promise<void> {
    const docRef = doc(db, 'gameSessions', sessionId);

    await runTransaction(db, async (transaction) => {
      const snapshot = await transaction.get(docRef);
      if (!snapshot.exists()) {
        throw new Error('Game session not found');
      }

      const rawData = snapshot.data() as Record<string, unknown>;
      const rawPlayers = Array.isArray(rawData.players) ? rawData.players : [];

      // Reset per-question answers while preserving the latest player combat/item fields.
      const resetAnswerPlayers = rawPlayers.map((rawPlayer) => {
        if (!rawPlayer || typeof rawPlayer !== 'object') {
          return rawPlayer;
        }

        const playerObject = { ...(rawPlayer as Record<string, unknown>) };
        delete playerObject.lastAnswerIndex;
        return this.deepCleanUndefined(playerObject);
      });

      const payload = this.buildGameSessionUpdatePayload({
        ...updates,
        players: resetAnswerPlayers as unknown as Player[]
      });

      transaction.update(docRef, payload);
    });
  }

  private async updateRunningStageSession(sessionId: string, updates: Partial<GameSession>): Promise<void> {
    const docRef = doc(db, 'gameSessions', sessionId);

    await runTransaction(db, async (transaction) => {
      const snapshot = await transaction.get(docRef);
      if (!snapshot.exists()) {
        throw new Error('Game session not found');
      }

      const rawData = snapshot.data() as Record<string, unknown>;
      const rawPlayers = Array.isArray(rawData.players) ? rawData.players : [];
      const resetPlayers = this.resetPlayerPositionsForArenaRound(rawPlayers);
      const payload = this.buildGameSessionUpdatePayload({
        ...updates,
        players: resetPlayers
      });

      transaction.update(docRef, {
        ...payload,
        playerPositions: this.buildArenaStartPositions(resetPlayers)
      });
    });
  }

  private async applyGameSessionUpdate(sessionId: string, updates: Partial<GameSession>): Promise<void> {
    if (updates.gameState === 'question') {
      await this.updateQuestionStageSession(sessionId, updates);
      return;
    }

    if (updates.gameState === 'running') {
      await this.updateRunningStageSession(sessionId, updates);
      return;
    }

    await this.updateGameSession(sessionId, updates);
  }

  private sanitizeForFirestore<T>(data: T): T {
    return JSON.parse(JSON.stringify(data));
  }

  private async updateSessionPlayer(
    sessionId: string,
    playerId: string,
    updater: (player: Player, session: Pick<GameSession, 'gameState' | 'players'>) => Player | null,
    options: { requireRunningState?: boolean } = {}
  ): Promise<boolean> {
    const docRef = doc(db, 'gameSessions', sessionId);

    return runTransaction(db, async (transaction) => {
      const snapshot = await transaction.get(docRef);
      if (!snapshot.exists()) {
        return false;
      }

      const rawData = snapshot.data();
      const validGameStates: GameSession['gameState'][] = ['lobby', 'countdown', 'question', 'running', 'scoring', 'leaderboard', 'bonus', 'finished'];
      const rawGameState = typeof rawData.gameState === 'string' ? rawData.gameState : 'lobby';
      const gameState: GameSession['gameState'] = validGameStates.includes(rawGameState as GameSession['gameState'])
        ? rawGameState as GameSession['gameState']
        : 'lobby';
      const players = Array.isArray(rawData.players) ? [...(rawData.players as Player[])] : [];

      if (options.requireRunningState && gameState !== 'running') {
        return false;
      }

      const playerIndex = players.findIndex(player => player?.id === playerId);
      if (playerIndex === -1) {
        return false;
      }

      const updatedPlayer = updater(players[playerIndex], {
        gameState,
        players
      });

      if (!updatedPlayer) {
        return false;
      }

      players[playerIndex] = this.deepCleanUndefined(updatedPlayer) as Player;

      transaction.update(docRef, this.deepCleanUndefined({
        players,
        updatedAt: serverTimestamp()
      }) as Record<string, unknown>);

      return true;
    });
  }

  private countQuestionsUpToStage(session: GameSession, stageIndex: number): number {
    if (stageIndex < 0) {
      return -1;
    }

    const stages = session.stages.slice(0, stageIndex + 1);
    const questionCount = stages.filter(stage => stage.type === 'question').length;
    return questionCount - 1;
  }

  /**
   * Calculate scores for all players at the end of a round
   * This runs server-side when teacher transitions to scoring phase
   */
  private calculateRoundScores(session: GameSession): typeof session.players {
    const currentQuestion = session.questions[session.currentQuestionIndex];
    if (!currentQuestion) {
      console.warn('⚠️ No current question found for scoring');
      return session.players;
    }

    const basePoints = 100;
    const questionMultiplier = currentQuestion.pointMultiplier ?? 1;

    // Calculate scores for each player
    const updatedPlayers = session.players.map(player => {
      const isCorrect = player.lastAnswerIndex === currentQuestion.correctAnswerIndex;

      if (player.lastAnswerIndex === undefined || player.lastAnswerIndex === null) {
        // Player didn't answer - keep score unchanged
        const newRoundScores = [...(player.roundScores || [])];
        newRoundScores[session.currentQuestionIndex] = 0;

        return {
          ...player,
          roundScores: newRoundScores,
          currentRoundScore: 0,
          score: player.score || 0
        };
      }

      // Calculate round score (simplified - no speed bonus on server)
      const roundScore = isCorrect ? (basePoints * questionMultiplier) : 0;

      // Update player's round scores array
      const newRoundScores = [...(player.roundScores || [])];
      newRoundScores[session.currentQuestionIndex] = roundScore;

      // Update cumulative total score
      const previousTotalScore = player.score || 0;
      const newTotalScore = previousTotalScore + roundScore;

      return {
        ...player,
        roundScores: newRoundScores,
        currentRoundScore: roundScore,
        score: newTotalScore
      };
    });

    return updatedPlayers;
  }

  private resetPlayerPositionsForArenaRound(players: unknown[]): Player[] {
    return players.map(player => {
      if (!player || typeof player !== 'object') {
        return player;
      }

      return this.deepCleanUndefined({
        ...(player as Record<string, unknown>),
        position: getArenaPlayerStartPosition()
      });
    }) as Player[];
  }

  private buildArenaStartPositions(players: Player[]): Record<string, { x: number; y: number }> {
    return players.reduce<Record<string, { x: number; y: number }>>((positions, player) => {
      if (player?.id) {
        positions[player.id] = getArenaPlayerStartPosition();
      }
      return positions;
    }, {});
  }

  private prepareStageTransition(session: GameSession, nextStageIndex: number, timestamp: Date): Partial<GameSession> {
    if (nextStageIndex >= session.stages.length) {
      return {
        currentStageIndex: session.stages.length,
        gameState: 'finished',
        status: 'completed',
        isActive: false,
        completedAt: timestamp,
        currentPhaseStartTime: null,
        currentPhaseEndTime: null,
        currentPhaseDurationSeconds: null,
        currentAnswerStats: null
      };
    }

    const nextStage = session.stages[nextStageIndex];

    if (nextStage.type === 'question' && nextStage.question) {
      const durations = this.getQuestionDurations(nextStage.question);
      const nextQuestionIndex = this.countQuestionsUpToStage(session, nextStageIndex);
      return {
        currentStageIndex: nextStageIndex,
        currentQuestionIndex: nextQuestionIndex >= 0 ? nextQuestionIndex : 0,
        currentQuestionStartTime: timestamp,
        gameState: 'question',
        status: 'active',
        isActive: true,
        currentPhaseStartTime: timestamp,
        currentPhaseEndTime: new Date(timestamp.getTime() + durations.reading * 1000),
        currentPhaseDurationSeconds: durations.reading,
        currentAnswerStats: new Array(nextStage.question.choices.length).fill(0)
      };
    }

    if (nextStage.type === 'bonus' && nextStage.bonusRound) {
      const duration = nextStage.bonusRound.selectionTimeSeconds ?? DEFAULT_BONUS_DURATION;
      return {
        currentStageIndex: nextStageIndex,
        gameState: 'bonus',
        status: 'active',
        isActive: true,
        currentPhaseStartTime: timestamp,
        currentPhaseEndTime: new Date(timestamp.getTime() + duration * 1000),
        currentPhaseDurationSeconds: duration,
        currentAnswerStats: null
      };
    }

    return {
      currentStageIndex: nextStageIndex,
      gameState: 'scoring',
      status: 'active',
      isActive: true,
      currentPhaseStartTime: timestamp,
      currentPhaseEndTime: null,
      currentPhaseDurationSeconds: null
    };
  }

  async signInWithEmailAndPassword(credentials: AuthCredentials): Promise<UserAuth> {
    try {
      console.log('🔐 Signing in with email and password...', credentials.email);
      console.log('🔥 Using auth instance:', !!auth);
      const userCredential = await firebaseSignIn(auth, credentials.email, credentials.password);
      const userAuth = this.mapFirebaseUserToUserAuth(userCredential.user);
      console.log('✅ Successfully signed in:', userAuth.email);
      return userAuth;
    } catch (error: unknown) {
      const err = error as { code?: string; message?: string };
      const errorMessage = err.message || String(error);
      console.error('❌ Sign in failed:', error);
      console.error('❌ Error code:', err.code);
      console.error('❌ Error message:', errorMessage);

      // Provide user-friendly error messages
      let friendlyMessage = errorMessage;
      switch (err.code) {
        case 'auth/user-not-found':
          friendlyMessage = 'No account found with this email address.';
          break;
        case 'auth/wrong-password':
          friendlyMessage = 'Incorrect password.';
          break;
        case 'auth/invalid-email':
          friendlyMessage = 'Invalid email address.';
          break;
        case 'auth/user-disabled':
          friendlyMessage = 'This account has been disabled.';
          break;
        case 'auth/too-many-requests':
          friendlyMessage = 'Too many failed attempts. Please try again later.';
          break;
        default:
          friendlyMessage = `Authentication error: ${errorMessage}`;
      }

      throw new Error(friendlyMessage);
    }
  }

  async signUpWithEmailAndPassword(signUpData: SignUpData): Promise<UserAuth> {
    try {
      console.log('📝 Creating new account for:', signUpData.email);
      console.log('🔥 Using auth instance:', !!auth);

      const userCredential = await createUserWithEmailAndPassword(
        auth,
        signUpData.email,
        signUpData.password
      );

      const userAuth = this.mapFirebaseUserToUserAuth(userCredential.user);
      console.log('✅ User account created:', userAuth.email);

      // Create teacher profile immediately after account creation
      await this.createTeacherProfile(signUpData, userAuth.uid);

      console.log('✅ Successfully created account and teacher profile:', userAuth.email);
      return userAuth;
    } catch (error: unknown) {
      const err = error as { code?: string; message?: string };
      const errorMessage = err.message || String(error);
      console.error('❌ Sign up failed:', error);
      console.error('❌ Error code:', err.code);
      console.error('❌ Error message:', errorMessage);

      // Provide user-friendly error messages
      let friendlyMessage = errorMessage;
      switch (err.code) {
        case 'auth/email-already-in-use':
          friendlyMessage = 'An account with this email already exists.';
          break;
        case 'auth/invalid-email':
          friendlyMessage = 'Invalid email address.';
          break;
        case 'auth/weak-password':
          friendlyMessage = 'Password is too weak. Please use at least 6 characters.';
          break;
        case 'auth/operation-not-allowed':
          friendlyMessage = 'Email/password accounts are not enabled. Please contact support.';
          break;
        default:
          friendlyMessage = `Account creation error: ${errorMessage}`;
      }

      throw new Error(friendlyMessage);
    }
  }

  async signInWithGoogle(): Promise<UserAuth> {
    try {
      console.log('🔐 Signing in with Google...');
      console.log('🔥 Using auth instance:', !!auth);
      console.log('🔥 Google provider configured:', !!this.googleProvider);

      const userCredential = await signInWithPopup(auth, this.googleProvider);
      const userAuth = this.mapFirebaseUserToUserAuth(userCredential.user);
      console.log('✅ Google authentication successful:', userAuth.email);

      // Check if teacher profile exists, create if not
      const teacherProfile = await this.getTeacherProfile(userAuth.uid);
      if (!teacherProfile) {
        console.log('📝 Creating teacher profile for Google user...');
        await this.createTeacherProfile({
          email: userAuth.email,
          password: '', // Not needed for Google auth
          displayName: userAuth.displayName
        }, userAuth.uid);
      }

      console.log('✅ Successfully signed in with Google:', userAuth.email);
      return userAuth;
    } catch (error: unknown) {
      const err = error as { code?: string; message?: string };
      const errorMessage = err.message || String(error);
      console.error('❌ Google sign in failed:', error);
      console.error('❌ Error code:', err.code);
      console.error('❌ Error message:', errorMessage);

      // Provide user-friendly error messages
      let friendlyMessage = errorMessage;
      switch (err.code) {
        case 'auth/popup-closed-by-user':
          friendlyMessage = 'Sign-in was cancelled.';
          break;
        case 'auth/popup-blocked':
          friendlyMessage = 'Sign-in popup was blocked by the browser.';
          break;
        case 'auth/operation-not-allowed':
          friendlyMessage = 'Google sign-in is not enabled. Please contact support.';
          break;
        case 'auth/unauthorized-domain':
          friendlyMessage = 'This domain is not authorized for Google sign-in.';
          break;
        default:
          friendlyMessage = `Google sign-in error: ${errorMessage}`;
      }

      throw new Error(friendlyMessage);
    }
  }

  async signOut(): Promise<void> {
    try {
      console.log('👋 Signing out...');
      await firebaseSignOut(auth);
      console.log('✅ Successfully signed out');
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('❌ Sign out failed:', errorMessage);
      throw new Error(`Sign out failed: ${errorMessage}`);
    }
  }

  async getCurrentUser(): Promise<UserAuth | null> {
    return new Promise((resolve) => {
      const unsubscribe = firebaseOnAuthStateChanged(auth, (firebaseUser) => {
        unsubscribe();
        if (firebaseUser) {
          resolve(this.mapFirebaseUserToUserAuth(firebaseUser));
        } else {
          resolve(null);
        }
      });
    });
  }

  onAuthStateChanged(callback: (user: UserAuth | null) => void): () => void {
    return firebaseOnAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser) {
        callback(this.mapFirebaseUserToUserAuth(firebaseUser));
      } else {
        callback(null);
      }
    });
  }

  async createTeacherProfile(userData: SignUpData, uid: string): Promise<TeacherProfile> {
    try {
      console.log('👨‍🏫 Creating teacher profile for:', userData.email);
      const teacherProfile: TeacherProfile = {
        uid,
        email: userData.email,
        displayName: userData.displayName,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      // Only add optional fields if they have values
      if (userData.institution && userData.institution.trim()) {
        teacherProfile.institution = userData.institution.trim();
      }
      if (userData.subject && userData.subject.trim()) {
        teacherProfile.subject = userData.subject.trim();
      }

      const firestoreData: Record<string, unknown> = {
        uid: teacherProfile.uid,
        email: teacherProfile.email,
        displayName: teacherProfile.displayName,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      // Only add optional fields if they exist in the profile
      if (teacherProfile.institution) {
        firestoreData.institution = teacherProfile.institution;
      }
      if (teacherProfile.subject) {
        firestoreData.subject = teacherProfile.subject;
      }

      await setDoc(doc(db, 'teacherProfiles', uid), firestoreData);

      console.log('✅ Teacher profile created successfully');
      return teacherProfile;
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('❌ Failed to create teacher profile:', errorMessage);
      throw new Error(`Failed to create teacher profile: ${errorMessage}`);
    }
  }

  async getTeacherProfile(uid: string): Promise<TeacherProfile | null> {
    try {
      const docRef = doc(db, 'teacherProfiles', uid);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        const profile: TeacherProfile = {
          uid: data.uid,
          email: data.email,
          displayName: data.displayName,
          photoURL: data.photoURL,
          institution: data.institution,
          subject: data.subject,
          createdAt: this.convertTimestamp(data.createdAt),
          updatedAt: this.convertTimestamp(data.updatedAt)
        };
        return profile;
      } else {
        return null;
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('❌ Failed to get teacher profile:', errorMessage);
      throw new Error(`Failed to get teacher profile: ${errorMessage}`);
    }
  }

  async updateTeacherProfile(uid: string, updates: Partial<TeacherProfile>): Promise<void> {
    try {
      console.log('📝 Updating teacher profile for:', uid);
      const docRef = doc(db, 'teacherProfiles', uid);
      await updateDoc(docRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
      console.log('✅ Teacher profile updated successfully');
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('❌ Failed to update teacher profile:', errorMessage);
      throw new Error(`Failed to update teacher profile: ${errorMessage}`);
    }
  }

  async createQuestionSet(questionSet: Omit<QuestionSet, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      console.log('📚 Creating question set:', questionSet.name);

      // Clean undefined values before sending to Firestore
      const cleanedData = this.cleanUndefined({
        ...questionSet,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      const docRef = await addDoc(collection(db, 'questionSets'), cleanedData);
      console.log('✅ Question set created with ID:', docRef.id);
      return docRef.id;
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('❌ Failed to create question set:', errorMessage);
      throw new Error(`Failed to create question set: ${errorMessage}`);
    }
  }

  async getTeacherQuestionSets(teacherUid: string): Promise<QuestionSet[]> {
    try {
      const q = query(
        collection(db, 'questionSets'),
        where('createdBy', '==', teacherUid)
      );
      const querySnapshot = await getDocs(q);

      const questionSets: QuestionSet[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        questionSets.push({
          id: doc.id,
          name: data.name,
          description: data.description,
          questions: data.questions || [],
          createdBy: data.createdBy,
          createdAt: this.convertTimestamp(data.createdAt),
          updatedAt: this.convertTimestamp(data.updatedAt),
          isActive: data.isActive,
          totalQuestions: data.totalQuestions,
          estimatedDuration: data.estimatedDuration,
          difficulty: data.difficulty,
          tags: data.tags,
          status: data.status || 'published', // Default to published for backward compatibility
          folderId: typeof data.folderId === 'string' ? data.folderId : undefined
        });
      });

      return questionSets;
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('❌ Failed to get question sets:', errorMessage);
      throw new Error(`Failed to get question sets: ${errorMessage}`);
    }
  }

  async getQuestionSet(questionSetId: string): Promise<QuestionSet | null> {
    try {
      console.log('📚 Fetching question set:', questionSetId);
      const docRef = doc(db, 'questionSets', questionSetId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        const questionSet: QuestionSet = {
          id: docSnap.id,
          name: data.name,
          description: data.description,
          questions: data.questions || [],
          createdBy: data.createdBy,
          createdAt: this.convertTimestamp(data.createdAt),
          updatedAt: this.convertTimestamp(data.updatedAt),
          isActive: data.isActive,
          totalQuestions: data.totalQuestions,
          estimatedDuration: data.estimatedDuration,
          difficulty: data.difficulty,
          tags: data.tags,
          status: data.status || 'published', // Default to published for backward compatibility
          folderId: typeof data.folderId === 'string' ? data.folderId : undefined
        };
        console.log('✅ Question set found');
        return questionSet;
      } else {
        console.log('❌ Question set not found');
        return null;
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('❌ Failed to get question set:', errorMessage);
      throw new Error(`Failed to get question set: ${errorMessage}`);
    }
  }

  async updateQuestionSet(questionSetId: string, updates: Partial<QuestionSet>): Promise<void> {
    try {
      console.log('📝 Updating question set:', questionSetId);
      console.log('📋 Updates before cleaning:', updates);
      const docRef = doc(db, 'questionSets', questionSetId);

      // Strategy: Remove undefined with JSON serialization, then re-add serverTimestamp
      // This is bulletproof because JSON.stringify automatically removes undefined
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { updatedAt, ...restUpdates } = updates;

      // Serialize and deserialize to remove all undefined values
      const serialized = JSON.stringify(restUpdates);
      const cleanedUpdates = JSON.parse(serialized);

      // Add back the serverTimestamp
      cleanedUpdates.updatedAt = serverTimestamp();

      console.log('✨ Updates after cleaning:', cleanedUpdates);
      console.log('🔍 Has description field?', 'description' in cleanedUpdates, cleanedUpdates.description);

      await updateDoc(docRef, cleanedUpdates);
      console.log('✅ Question set updated successfully');
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('❌ Failed to update question set:', errorMessage);
      console.error('❌ Full error:', error);
      throw new Error(`Failed to update question set: ${errorMessage}`);
    }
  }

  async deleteQuestionSet(questionSetId: string): Promise<void> {
    try {
      console.log('🗑️ Deleting question set:', questionSetId);
      const docRef = doc(db, 'questionSets', questionSetId);
      await deleteDoc(docRef);
      console.log('✅ Question set deleted successfully');
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('❌ Failed to delete question set:', errorMessage);
      throw new Error(`Failed to delete question set: ${errorMessage}`);
    }
  }

  // Folder Management Methods
  async createFolder(folder: Omit<QuestionFolder, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      console.log('📁 Creating folder:', folder.name);

      const cleanedData = this.cleanUndefined({
        ...folder,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      const docRef = await addDoc(collection(db, 'questionFolders'), cleanedData);
      console.log('✅ Folder created with ID:', docRef.id);
      return docRef.id;
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('❌ Failed to create folder:', errorMessage);
      throw new Error(`Failed to create folder: ${errorMessage}`);
    }
  }

  async getTeacherFolders(teacherUid: string): Promise<QuestionFolder[]> {
    try {
      const q = query(
        collection(db, 'questionFolders'),
        where('createdBy', '==', teacherUid)
      );
      const querySnapshot = await getDocs(q);

      const folders: QuestionFolder[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        // Filter out archived folders on the client side
        if (data.isArchived === true) {
          return; // Skip archived folders
        }
        folders.push({
          id: doc.id,
          name: data.name,
          description: data.description,
          createdBy: data.createdBy,
          parentFolderId: data.parentFolderId,
          color: data.color,
          icon: data.icon,
          createdAt: this.convertTimestamp(data.createdAt),
          updatedAt: this.convertTimestamp(data.updatedAt),
          isArchived: data.isArchived
        });
      });

      return folders;
    } catch (error: unknown) {

      const errorMessage = error instanceof Error ? error.message : String(error);

      console.error('❌ Failed to get folders:', errorMessage);

      throw new Error(`Failed to get folders: ${errorMessage}`);

    }
  }

  async getFolder(folderId: string): Promise<QuestionFolder | null> {
    try {
      console.log('📁 Fetching folder:', folderId);
      const docRef = doc(db, 'questionFolders', folderId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        const folder: QuestionFolder = {
          id: docSnap.id,
          name: data.name,
          description: data.description,
          createdBy: data.createdBy,
          parentFolderId: data.parentFolderId,
          color: data.color,
          icon: data.icon,
          createdAt: this.convertTimestamp(data.createdAt),
          updatedAt: this.convertTimestamp(data.updatedAt),
          isArchived: data.isArchived
        };
        console.log('✅ Folder found');
        return folder;
      } else {
        console.log('❌ Folder not found');
        return null;
      }
    } catch (error: unknown) {

      const errorMessage = error instanceof Error ? error.message : String(error);

      console.error('❌ Failed to get folder:', errorMessage);

      throw new Error(`Failed to get folder: ${errorMessage}`);

    }
  }

  async updateFolder(folderId: string, updates: Partial<QuestionFolder>): Promise<void> {
    try {
      console.log('📝 Updating folder:', folderId);
      const docRef = doc(db, 'questionFolders', folderId);

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { updatedAt, ...restUpdates } = updates;
      const serialized = JSON.stringify(restUpdates);
      const cleanedUpdates = JSON.parse(serialized);
      cleanedUpdates.updatedAt = serverTimestamp();

      await updateDoc(docRef, cleanedUpdates);
      console.log('✅ Folder updated successfully');
    } catch (error: unknown) {

      const errorMessage = error instanceof Error ? error.message : String(error);

      console.error('❌ Failed to update folder:', errorMessage);

      throw new Error(`Failed to update folder: ${errorMessage}`);

    }
  }

  async deleteFolder(folderId: string): Promise<void> {
    try {
      console.log('🗑️ Deleting folder:', folderId);
      const currentUser = auth.currentUser;

      if (!currentUser) {
        throw new Error('You must be signed in to delete folders.');
      }

      // Firestore list queries must be scoped to the signed-in teacher; rules are not post-filters.
      const [teacherQuestionSets, teacherFolders] = await Promise.all([
        this.getTeacherQuestionSets(currentUser.uid),
        this.getTeacherFolders(currentUser.uid)
      ]);

      if (teacherQuestionSets.some((questionSet) => questionSet.folderId === folderId)) {
        throw new Error('Cannot delete folder that contains question sets. Move or delete them first.');
      }

      const hasActiveSubFolders = teacherFolders.some(
        (folder) => folder.parentFolderId === folderId && folder.isArchived !== true
      );
      if (hasActiveSubFolders) {
        throw new Error('Cannot delete folder that contains subfolders. Move or delete them first.');
      }

      const docRef = doc(db, 'questionFolders', folderId);
      await deleteDoc(docRef);
      console.log('✅ Folder deleted successfully');
    } catch (error: unknown) {

      const errorMessage = error instanceof Error ? error.message : String(error);

      console.error('❌ Failed to delete folder:', errorMessage);

      throw new Error(`Failed to delete folder: ${errorMessage}`);

    }
  }

  async moveQuestionSetToFolder(questionSetId: string, folderId: string | null): Promise<void> {
    try {
      console.log(`📦 Moving question set ${questionSetId} to folder ${folderId || 'root'}`);
      await this.updateQuestionSet(questionSetId, { folderId: folderId || undefined });
      console.log('✅ Question set moved successfully');
    } catch (error: unknown) {

      const errorMessage = error instanceof Error ? error.message : String(error);

      console.error('❌ Failed to move question set:', errorMessage);

      throw new Error(`Failed to move question set: ${errorMessage}`);

    }
  }

  async moveFolderToFolder(folderId: string, parentFolderId: string | null): Promise<void> {
    try {
      console.log(`📁 Moving folder ${folderId} to parent ${parentFolderId || 'root'}`);

      // Prevent circular references
      if (folderId === parentFolderId) {
        throw new Error('Cannot move folder into itself');
      }

      // Check if moving to a descendant (would create a cycle)
      if (parentFolderId) {
        const isDescendant = await this.isFolderDescendant(folderId, parentFolderId);
        if (isDescendant) {
          throw new Error('Cannot move folder into its own descendant');
        }
      }

      await this.updateFolder(folderId, { parentFolderId: parentFolderId || undefined });
      console.log('✅ Folder moved successfully');
    } catch (error: unknown) {

      const errorMessage = error instanceof Error ? error.message : String(error);

      console.error('❌ Failed to move folder:', errorMessage);

      throw new Error(`Failed to move folder: ${errorMessage}`);

    }
  }

  private async isFolderDescendant(ancestorId: string, possibleDescendantId: string): Promise<boolean> {
    let currentId: string | undefined = possibleDescendantId;
    const visited = new Set<string>();

    while (currentId) {
      if (currentId === ancestorId) {
        return true;
      }
      if (visited.has(currentId)) {
        // Circular reference detected
        console.warn('⚠️ Circular reference detected in folder structure');
        return false;
      }
      visited.add(currentId);

      const folder = await this.getFolder(currentId);
      currentId = folder?.parentFolderId;
    }

    return false;
  }

  // New Teacher Game Control Methods
  async startGamePhase(sessionId: string): Promise<void> {
    try {
      console.log('🎮 Starting game phase for session:', sessionId);

      const session = await this.getGameSessionById(sessionId);
      if (!session) {
        throw new Error('Game session not found');
      }

      const now = new Date();

      if (!Array.isArray(session.stages) || session.stages.length === 0) {
        const firstQuestion = session.questions[0];
        if (!firstQuestion) {
          throw new Error('No questions available in this session');
        }

        const durations = this.getQuestionDurations(firstQuestion);

        await this.applyGameSessionUpdate(sessionId, {
          gameState: 'question',
          status: 'active',
          isActive: true,
          currentQuestionIndex: 0,
          startedAt: now,
          currentQuestionStartTime: now,
          currentPhaseStartTime: now,
          currentPhaseEndTime: new Date(now.getTime() + durations.reading * 1000),
          currentPhaseDurationSeconds: durations.reading,
          currentAnswerStats: new Array(firstQuestion.choices.length).fill(0),
          currentPhase: {
            type: 'question',
            questionIndex: 0,
            startTime: now,
            duration: durations.reading,
            autoAdvance: false
          }
        });

        console.log('✅ Game phase started successfully (fallback without stages)');
        return;
      }

      const safeStageIndex = session.currentStageIndex >= 0 && session.currentStageIndex < session.stages.length
        ? session.currentStageIndex
        : 0;

      const stage = session.stages[safeStageIndex];
      const stageUpdates = this.prepareStageTransition(session, safeStageIndex, now);
      const questionDurations = stage.type === 'question' ? this.getQuestionDurations(stage.question) : null;

      const phaseDuration = stageUpdates.currentPhaseDurationSeconds
        ?? (stage.type === 'bonus'
          ? stage.bonusRound?.selectionTimeSeconds ?? DEFAULT_BONUS_DURATION
          : stage.type === 'question'
            ? questionDurations?.reading ?? DEFAULT_TIMER_CONFIG.questionPhaseSeconds
            : null);

      const currentPhase = (() => {
        if (stage.type === 'question') {
          const questionIndex = stageUpdates.currentQuestionIndex ?? stage.questionIndex ?? 0;
          const duration = phaseDuration ?? DEFAULT_TIMER_CONFIG.questionPhaseSeconds;

          return {
            type: 'question' as const,
            questionIndex,
            startTime: now,
            duration,
            autoAdvance: false
          };
        }

        if (stage.type === 'bonus') {
          const duration = phaseDuration ?? DEFAULT_BONUS_DURATION;

          return {
            type: 'bonus' as const,
            startTime: now,
            duration,
            autoAdvance: false
          };
        }

        return undefined;
      })();

      await this.applyGameSessionUpdate(sessionId, {
        ...stageUpdates,
        status: 'active',
        startedAt: now,
        ...(currentPhase ? { currentPhase } : {})
      });

      console.log('✅ Game phase started successfully');
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('❌ Failed to start game phase:', errorMessage);
      throw error;
    }
  }

  async advanceToNextPhase(sessionId: string): Promise<void> {
    try {
      console.log('⏭️ Advancing to next phase for session:', sessionId);

      const session = await this.getGameSessionById(sessionId);
      if (!session) {
        throw new Error('Game session not found');
      }

      const currentQuestion = session.questions[session.currentQuestionIndex];

      switch (session.gameState) {
        case 'question':
          // Move to arena/running phase
          await this.applyGameSessionUpdate(sessionId, {
            gameState: 'running',
            currentPhase: {
              type: 'running',
              questionIndex: session.currentQuestionIndex,
              startTime: new Date(),
              duration: currentQuestion?.arenaPlayTimeSeconds || 8,
              autoAdvance: false
            }
          });
          break;

        case 'running':
          // Move to scoring phase
          await this.applyGameSessionUpdate(sessionId, {
            gameState: 'scoring',
            currentPhase: {
              type: 'scoring',
              questionIndex: session.currentQuestionIndex,
              startTime: new Date(),
              autoAdvance: false
            }
          });
          break;

        case 'bonus': {
          // Move to next question or finish
          const nextQuestionIndex = session.currentQuestionIndex + 1;
          if (nextQuestionIndex < session.questions.length) {
            const nextQuestion = session.questions[nextQuestionIndex];
            await this.applyGameSessionUpdate(sessionId, {
              gameState: 'question',
              currentQuestionIndex: nextQuestionIndex,
              currentPhase: {
                type: 'question',
                questionIndex: nextQuestionIndex,
                startTime: new Date(),
                duration: nextQuestion.questionReadingTimeSeconds || 10,
                autoAdvance: false
              }
            });
          } else {
            await this.applyGameSessionUpdate(sessionId, {
              gameState: 'finished',
              status: 'completed',
              completedAt: new Date(),
              currentPhase: {
                type: 'finished',
                startTime: new Date(),
                autoAdvance: false
              }
            });
          }
          break;
        }

        default:
          throw new Error(`Cannot advance from current state: ${session.gameState}`);
      }

      console.log('✅ Phase advanced successfully');
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('❌ Failed to advance phase:', errorMessage);
      throw error;
    }
  }

  async proceedAfterScoring(sessionId: string): Promise<void> {
    try {
      console.log('📊 Proceeding after scoring for session:', sessionId);

      const session = await this.getGameSessionById(sessionId);
      if (!session) {
        throw new Error('Game session not found');
      }

      const nextQuestionIndex = session.currentQuestionIndex + 1;

      // Check if there are more questions
      if (nextQuestionIndex < session.questions.length) {
        const nextQuestion = session.questions[nextQuestionIndex];

        // Check if there's a bonus round before the next question
        // For now, assume no bonus rounds - go directly to next question
        await this.applyGameSessionUpdate(sessionId, {
          gameState: 'question',
          currentQuestionIndex: nextQuestionIndex,
          currentPhase: {
            type: 'question',
            questionIndex: nextQuestionIndex,
            startTime: new Date(),
            duration: nextQuestion.questionReadingTimeSeconds || 10,
            autoAdvance: false
          }
        });
      } else {
        // Game finished
        await this.applyGameSessionUpdate(sessionId, {
          gameState: 'finished',
          status: 'completed',
          completedAt: new Date(),
          currentPhase: {
            type: 'finished',
            startTime: new Date(),
            autoAdvance: false
          }
        });
      }

      console.log('✅ Proceeded after scoring successfully');
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('❌ Failed to proceed after scoring:', errorMessage);
      throw error;
    }
  }

  async skipBonusPhase(sessionId: string): Promise<void> {
    try {
      console.log('⏭️ Skipping bonus phase for session:', sessionId);

      // Same logic as proceeding after scoring - advance to next question
      await this.proceedAfterScoring(sessionId);

      console.log('✅ Bonus phase skipped successfully');
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('❌ Failed to skip bonus phase:', errorMessage);
      throw error;
    }
  }

  async setPlayerReadyStatus(sessionId: string, playerId: string, isReady: boolean): Promise<void> {
    try {
      console.log('🎯 Updating ready status for player', playerId, 'in session', sessionId, '=>', isReady);

      const session = await this.getGameSessionById(sessionId);
      if (!session) {
        throw new Error('Game session not found');
      }

      const playerExists = session.players.some(player => player.id === playerId);
      if (!playerExists) {
        throw new Error('Player not found in session');
      }

      const updatedPlayers = session.players.map(player =>
        player.id === playerId ? { ...player, isReady } : player
      );

      await this.applyGameSessionUpdate(sessionId, { players: updatedPlayers });
      console.log('✅ Player ready status updated');
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('❌ Failed to update player ready status:', errorMessage);
      throw new Error(`Failed to update player ready status: ${errorMessage}`);
    }
  }

  async submitStudentAnswer(sessionId: string, playerId: string, answerIndex: number, questionIndex: number): Promise<void> {
    try {
      const docRef = doc(db, 'gameSessions', sessionId);

      await runTransaction(db, async (transaction) => {
        const snapshot = await transaction.get(docRef);
        if (!snapshot.exists()) {
          throw new Error('Game session not found');
        }

        const rawData = snapshot.data();
        const rawGameState = typeof rawData.gameState === 'string' ? rawData.gameState : 'lobby';

        if (!['question', 'running'].includes(rawGameState)) {
          console.warn('⚠️ Answer submitted but game is not in question/running state:', rawGameState);
          return;
        }

        const currentQuestionIndex = typeof rawData.currentQuestionIndex === 'number'
          ? rawData.currentQuestionIndex
          : 0;

        if (questionIndex !== currentQuestionIndex) {
          logger.debug('Ignoring stale answer submission for previous question', {
            sessionId,
            playerId,
            answerIndex,
            submittedQuestionIndex: questionIndex,
            currentQuestionIndex,
            gameState: rawGameState
          });
          return;
        }

        const players = Array.isArray(rawData.players) ? [...(rawData.players as Player[])] : [];
        const playerIndex = players.findIndex(player => player?.id === playerId);
        if (playerIndex === -1) {
          throw new Error('Player not found in session');
        }

        const questions = Array.isArray(rawData.questions) ? (rawData.questions as Question[]) : [];
        const currentQuestion = questions[currentQuestionIndex];

        let currentAnswerStats = Array.isArray(rawData.currentAnswerStats)
          ? [...(rawData.currentAnswerStats as number[])]
          : null;

        if (!currentAnswerStats) {
          if (currentQuestion) {
            currentAnswerStats = new Array(currentQuestion.choices.length).fill(0);
          } else {
            console.warn('⚠️ No current question found to initialize answer stats');
            return;
          }
        }

        if (answerIndex < 0 || answerIndex >= currentAnswerStats.length) {
          throw new Error(`Invalid answer index: ${answerIndex}`);
        }

        const updatedAnswerStats = [...currentAnswerStats];
        const player = players[playerIndex];

        if (player.lastAnswerIndex !== undefined && player.lastAnswerIndex !== null) {
          if (player.lastAnswerIndex >= 0 && player.lastAnswerIndex < updatedAnswerStats.length) {
            updatedAnswerStats[player.lastAnswerIndex] = Math.max(0, updatedAnswerStats[player.lastAnswerIndex] - 1);
          }
        }

        updatedAnswerStats[answerIndex]++;

        players[playerIndex] = this.deepCleanUndefined({
          ...player,
          lastAnswerIndex: answerIndex
        }) as Player;

        transaction.update(docRef, this.deepCleanUndefined({
          currentAnswerStats: updatedAnswerStats,
          players,
          updatedAt: serverTimestamp()
        }) as Record<string, unknown>);
      });
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      throw new Error(`Failed to submit student answer: ${errorMessage}`);
    }
  }

  async clearStudentAnswer(sessionId: string, playerId: string, questionIndex: number): Promise<void> {
    try {
      const docRef = doc(db, 'gameSessions', sessionId);

      await runTransaction(db, async (transaction) => {
        const snapshot = await transaction.get(docRef);
        if (!snapshot.exists()) {
          throw new Error('Game session not found');
        }

        const rawData = snapshot.data();
        const rawGameState = typeof rawData.gameState === 'string' ? rawData.gameState : 'lobby';
        const currentQuestionIndex = typeof rawData.currentQuestionIndex === 'number'
          ? rawData.currentQuestionIndex
          : 0;

        if (questionIndex !== currentQuestionIndex) {
          logger.debug('Ignoring stale answer clear for previous question', {
            sessionId,
            playerId,
            submittedQuestionIndex: questionIndex,
            currentQuestionIndex,
            gameState: rawGameState
          });
          return;
        }

        const players = Array.isArray(rawData.players) ? [...(rawData.players as Player[])] : [];
        const playerIndex = players.findIndex(player => player?.id === playerId);
        if (playerIndex === -1) {
          throw new Error('Player not found in session');
        }

        const player = players[playerIndex];
        const previousAnswerIndex = player.lastAnswerIndex;
        if (previousAnswerIndex === undefined || previousAnswerIndex === null) {
          return;
        }

        let updatedAnswerStats = Array.isArray(rawData.currentAnswerStats)
          ? [...(rawData.currentAnswerStats as number[])]
          : null;

        if (updatedAnswerStats) {
          if (previousAnswerIndex >= 0 && previousAnswerIndex < updatedAnswerStats.length) {
            updatedAnswerStats[previousAnswerIndex] = Math.max(0, updatedAnswerStats[previousAnswerIndex] - 1);
          } else {
            console.warn('⚠️ Previous answer index out of range when clearing:', previousAnswerIndex);
          }
        }

        players[playerIndex] = this.deepCleanUndefined({
          ...player,
          lastAnswerIndex: undefined
        }) as Player;

        const updates: Record<string, unknown> = {
          players,
          updatedAt: serverTimestamp()
        };

        if (updatedAnswerStats) {
          updates.currentAnswerStats = updatedAnswerStats;
        }

        transaction.update(docRef, this.deepCleanUndefined(updates) as Record<string, unknown>);
      });
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      throw new Error(`Failed to clear student answer: ${errorMessage}`);
    }
  }

  async updatePlayerPosition(sessionId: string, playerId: string, position: { x: number; y: number }): Promise<void> {
    try {
      await this.updateSessionPlayer(
        sessionId,
        playerId,
        (player) => {
          if (player.position?.x === position.x && player.position?.y === position.y) {
            return null;
          }

          return {
            ...player,
            position: { x: position.x, y: position.y }
          };
        },
        { requireRunningState: true }
      );
    } catch {
      // Silently fail position updates to avoid spamming console/errors
      // Position updates happen frequently and occasional failures are acceptable
    }
  }

  async updatePlayerPositionDirect(sessionId: string, playerId: string, position: { x: number; y: number }): Promise<void> {
    try {
      const docRef = doc(db, 'gameSessions', sessionId);
      await updateDoc(docRef, {
        [`playerPositions.${playerId}`]: { x: position.x, y: position.y },
        updatedAt: serverTimestamp()
      });
    } catch {
      // Position updates are best-effort during arena play
    }
  }

  async updatePlayerItems(sessionId: string, playerId: string, updates: {
    netCharges?: number;
    grenadeCharges?: number;
    turboAvailable?: boolean;
    turboUsed?: boolean;
    shoesEffectNextRound?: boolean;
    lastGrenadeThrow?: GrenadeThrowEvent | null;
    position?: { x: number; y: number };
  }): Promise<void> {
    try {
      type PlayerItemUpdates = Pick<
        Player,
        | 'netCharges'
        | 'grenadeCharges'
        | 'turboAvailable'
        | 'turboUsed'
        | 'shoesEffectNextRound'
        | 'lastGrenadeThrow'
        | 'position'
      >;

      const sanitizedUpdates: Partial<PlayerItemUpdates> = { ...updates };

      if (sanitizedUpdates.netCharges !== undefined) {
        const parsed = Number(sanitizedUpdates.netCharges);
        sanitizedUpdates.netCharges = Number.isFinite(parsed) ? Math.max(0, Math.floor(parsed)) : 0;
      }

      if (sanitizedUpdates.grenadeCharges !== undefined) {
        const parsed = Number(sanitizedUpdates.grenadeCharges);
        sanitizedUpdates.grenadeCharges = Number.isFinite(parsed) ? Math.max(0, Math.floor(parsed)) : 0;
      }

      if (updates.lastGrenadeThrow) {
        const event = updates.lastGrenadeThrow;
        sanitizedUpdates.lastGrenadeThrow = {
          id: event.id,
          thrownAt: event.thrownAt,
          startPosition: { x: event.startPosition.x, y: event.startPosition.y },
          velocity: { x: event.velocity.x, y: event.velocity.y },
          chargeAmount: event.chargeAmount,
          throwDistance: event.throwDistance,
          bouncesRemaining: event.bouncesRemaining
        };
      } else if (updates.lastGrenadeThrow === null) {
        sanitizedUpdates.lastGrenadeThrow = null;
      }

      if (updates.position) {
        const parsedX = Number(updates.position.x);
        const parsedY = Number(updates.position.y);

        if (Number.isFinite(parsedX) && Number.isFinite(parsedY)) {
          sanitizedUpdates.position = { x: parsedX, y: parsedY };
        }
      }

      const hasFieldsToUpdate = Object.keys(sanitizedUpdates).length > 0;
      if (!hasFieldsToUpdate) {
        logger.debug('Skipping player item sync because no fields were provided', { sessionId, playerId });
        return;
      }

      await this.updateSessionPlayer(sessionId, playerId, (player) => {
        const updateKeys = Object.keys(sanitizedUpdates) as (keyof PlayerItemUpdates)[];
        const hasMeaningfulChange = updateKeys.some((key) => {
          const newValue = sanitizedUpdates[key];
          const previousValue = player[key];

          if (key === 'lastGrenadeThrow') {
            const existingEvent = player.lastGrenadeThrow ?? null;
            const incomingEvent = (newValue ?? null) as Player['lastGrenadeThrow'];

            if (!incomingEvent && !existingEvent) {
              return false;
            }

            if (!incomingEvent || !existingEvent) {
              return true;
            }

            return incomingEvent.id !== existingEvent.id || incomingEvent.thrownAt !== existingEvent.thrownAt;
          }

          if (key === 'position') {
            const previousPosition = player.position;
            const incomingPosition = newValue as Player['position'] | undefined;

            if (!incomingPosition) {
              return false;
            }

            return (
              previousPosition.x !== incomingPosition.x ||
              previousPosition.y !== incomingPosition.y
            );
          }

          return previousValue !== newValue;
        });

        if (!hasMeaningfulChange) {
          return null;
        }

        return {
          ...player,
          ...sanitizedUpdates
        };
      });
    } catch (error: unknown) {
      logger.debug('Failed to update player items', error);
      throw error;
    }
  }

  async syncNetThrow(sessionId: string, netProjectile: import('@/types').NetProjectile): Promise<void> {
    try {
      const serializedNet = {
        id: netProjectile.id,
        thrownBy: netProjectile.thrownBy,
        startPosition: { x: netProjectile.startPosition.x, y: netProjectile.startPosition.y },
        currentPosition: { x: netProjectile.currentPosition.x, y: netProjectile.currentPosition.y },
        targetPosition: { x: netProjectile.targetPosition.x, y: netProjectile.targetPosition.y },
        createdAt: netProjectile.createdAt,
        isActive: netProjectile.isActive,
        ...(netProjectile.deactivatedAt !== undefined && { deactivatedAt: netProjectile.deactivatedAt })
      };

      const docRef = doc(db, 'gameSessions', sessionId);
      await updateDoc(docRef, {
        activeNetProjectiles: arrayUnion(serializedNet),
        updatedAt: serverTimestamp()
      });

      console.log(`🕸️ Net ${netProjectile.id} synced to Firebase`);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('❌ Failed to sync net throw:', errorMessage, error);
    }
  }

  async removeNetProjectile(sessionId: string, netId: string): Promise<void> {
    try {
      const session = await this.getGameSessionById(sessionId);
      if (!session || !session.activeNetProjectiles) {
        return;
      }

      const updatedNets = session.activeNetProjectiles.filter(net => net.id !== netId);

      await this.applyGameSessionUpdate(sessionId, {
        activeNetProjectiles: updatedNets
      });

      console.log(`🗑️ Net ${netId} removed from Firebase`);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('❌ Failed to remove net:', errorMessage);
      // Non-critical, so don't throw
    }
  }

  async updatePlayerImmobilization(sessionId: string, playerId: string, isImmobilized: boolean, immobilizedUntil?: number): Promise<void> {
    try {
      await this.updateSessionPlayer(
        sessionId,
        playerId,
        (player) => {
          const updatedPlayer: Player = {
            ...player,
            isImmobilized
          };

          if (isImmobilized && immobilizedUntil !== undefined) {
            updatedPlayer.immobilizedUntil = immobilizedUntil;
          } else {
            delete updatedPlayer.immobilizedUntil;
          }

          const hasSameValue = player.isImmobilized === updatedPlayer.isImmobilized
            && player.immobilizedUntil === updatedPlayer.immobilizedUntil;

          return hasSameValue ? null : updatedPlayer;
        },
        { requireRunningState: true }
      );
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      logger.debug('Failed to sync player immobilization', errorMessage);
      // Non-critical, so don't throw
    }
  }

  async updatePlayerPushEffect(
    sessionId: string,
    playerId: string,
    isBeingPushed: boolean,
    pushVelocity?: { x: number; y: number },
    pushEndTime?: number
  ): Promise<void> {
    try {
      await this.updateSessionPlayer(
        sessionId,
        playerId,
        (player) => {
          const updatedPlayer: Player = {
            ...player,
            isBeingPushed
          };

          if (isBeingPushed && pushVelocity !== undefined && pushEndTime !== undefined) {
            updatedPlayer.pushVelocity = pushVelocity;
            updatedPlayer.pushEndTime = pushEndTime;
          } else {
            delete updatedPlayer.pushVelocity;
            delete updatedPlayer.pushEndTime;
          }

          const sameVelocity = (player.pushVelocity?.x ?? null) === (updatedPlayer.pushVelocity?.x ?? null)
            && (player.pushVelocity?.y ?? null) === (updatedPlayer.pushVelocity?.y ?? null);
          const hasSameValue = player.isBeingPushed === updatedPlayer.isBeingPushed
            && sameVelocity
            && player.pushEndTime === updatedPlayer.pushEndTime;

          return hasSameValue ? null : updatedPlayer;
        },
        { requireRunningState: true }
      );
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      logger.debug('Failed to sync player push effect', errorMessage);
      // Non-critical, so don't throw
    }
  }

  async batchUpdatePlayerPushEffects(
    sessionId: string,
    effects: Array<{ playerId: string; isBeingPushed: boolean; pushVelocity?: { x: number; y: number }; pushEndTime?: number }>
  ): Promise<void> {
    if (effects.length === 0) return;

    try {
      const docRef = doc(db, 'gameSessions', sessionId);

      await runTransaction(db, async (transaction) => {
        const snapshot = await transaction.get(docRef);
        if (!snapshot.exists()) return;

        const rawData = snapshot.data();
        const players = Array.isArray(rawData.players) ? [...(rawData.players as Player[])] : [];

        const effectMap = new Map(effects.map(e => [e.playerId, e]));
        let hasChanges = false;

        for (let i = 0; i < players.length; i++) {
          const effect = effectMap.get(players[i]?.id);
          if (!effect) continue;

          const player = { ...players[i] };
          player.isBeingPushed = effect.isBeingPushed;

          if (effect.isBeingPushed && effect.pushVelocity !== undefined && effect.pushEndTime !== undefined) {
            player.pushVelocity = effect.pushVelocity;
            player.pushEndTime = effect.pushEndTime;
          } else {
            delete player.pushVelocity;
            delete player.pushEndTime;
          }

          players[i] = player;
          hasChanges = true;
        }

        if (hasChanges) {
          transaction.update(docRef, this.deepCleanUndefined({
            players,
            updatedAt: serverTimestamp()
          }) as Record<string, unknown>);
        }
      });
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      logger.debug('Failed to batch sync push effects', errorMessage);
    }
  }

  async sendPasswordResetEmail(email: string): Promise<void> {
    try {
      console.log('📧 Sending password reset email to:', email);
      await firebaseSendPasswordResetEmail(auth, email);
      console.log('✅ Password reset email sent');
    } catch (error: unknown) {

      const errorMessage = error instanceof Error ? error.message : String(error);

      console.error('❌ Failed to send password reset email:', errorMessage);

      throw new Error(`Failed to send password reset email: ${errorMessage}`);

    }
  }

  async updateUserPassword(newPassword: string): Promise<void> {
    try {
      if (!auth.currentUser) {
        throw new Error('No user signed in');
      }
      console.log('🔒 Updating user password...');
      await updatePassword(auth.currentUser, newPassword);
      console.log('✅ Password updated successfully');
    } catch (error: unknown) {

      const errorMessage = error instanceof Error ? error.message : String(error);

      console.error('❌ Failed to update password:', errorMessage);

      throw new Error(`Failed to update password: ${errorMessage}`);

    }
  }

  async deleteUserAccount(): Promise<void> {
    try {
      if (!auth.currentUser) {
        throw new Error('No user signed in');
      }
      console.log('🗑️ Deleting user account...');

      // Delete teacher profile first
      await deleteDoc(doc(db, 'teacherProfiles', auth.currentUser.uid));

      // Delete the user account
      await deleteUser(auth.currentUser);
      console.log('✅ User account deleted successfully');
    } catch (error: unknown) {

      const errorMessage = error instanceof Error ? error.message : String(error);

      console.error('❌ Failed to delete user account:', errorMessage);

      throw new Error(`Failed to delete user account: ${errorMessage}`);

    }
  }

  // Game Session Management Methods

  private generateGameCode(): string {
    // Generate a 6-digit game code
    return Math.floor(100000 + Math.random() * 900000).toString();
  }

  private async ensureUniqueGameCode(): Promise<string> {
    let gameCode: string;
    let attempts = 0;
    const maxAttempts = 10;

    do {
      gameCode = this.generateGameCode();
      const existingSession = await this.getGameSession(gameCode);
      if (!existingSession) {
        return gameCode;
      }
      attempts++;
    } while (attempts < maxAttempts);

    throw new Error('Failed to generate unique game code after multiple attempts');
  }

  async createGameSession(sessionData: SessionCreationData, teacherId: string): Promise<GameSession> {
    try {
      console.log('🎮 Creating game session for teacher:', teacherId);

      // Get question set details
      const questionSet = await this.getQuestionSet(sessionData.questionSetId);
      if (!questionSet) {
        throw new Error('Question set not found');
      }

      // Get teacher profile for name
      console.log('🔍 Looking up teacher profile for ID:', teacherId);
      let teacherProfile = await this.getTeacherProfile(teacherId);
      console.log('📋 Teacher profile result:', teacherProfile);

      if (!teacherProfile) {
        console.error('❌ Teacher profile not found for ID:', teacherId);
        console.log('🔧 Available teacher profiles in database:', await this.listAllTeacherProfiles());

        // Try to get current user info and create profile if needed
        const currentUser = await this.getCurrentUser();
        console.log('🔍 Current user for auto-repair:', currentUser ? { uid: currentUser.uid, email: currentUser.email } : 'null');

        if (currentUser && currentUser.uid === teacherId) {
          console.log('🔧 Attempting to create missing teacher profile...');
          try {
            const profileData = {
              email: currentUser.email,
              password: '', // Not needed for profile creation
              displayName: currentUser.displayName || 'Teacher'
            };
            console.log('📝 Profile data for creation:', profileData);

            teacherProfile = await this.createTeacherProfile(profileData, teacherId);
            console.log('✅ Created missing teacher profile:', teacherProfile);
          } catch (profileError: unknown) {
            const profErrorMessage = profileError instanceof Error ? profileError.message : String(profileError);
            console.error('❌ Failed to create teacher profile:', profileError);
            console.error('❌ Profile creation error details:', profErrorMessage);
            throw new Error(`Teacher profile not found and could not be created: ${profErrorMessage}`);
          }
        } else {
          throw new Error('Teacher profile not found');
        }
      }

      // Generate unique game code
      const gameCode = await this.ensureUniqueGameCode();

      const transformed = transformQuestionSetToGameData(questionSet);

      const questions: Question[] = transformed.questions.map(question =>
        this.sanitizeForFirestore(question) as Question
      );

      const stages: SessionStage[] = transformed.stages.map(stage => {
        if (stage.type === 'question') {
          const questionIdx = typeof stage.questionIndex === 'number' ? stage.questionIndex : 0;
          const questionRef = questions[questionIdx] ?? (this.sanitizeForFirestore(stage.question) as Question);

          return {
            ...stage,
            question: questionRef
          };
        }

        return {
          ...stage,
          bonusRound: stage.bonusRound
            ? {
                ...stage.bonusRound,
                createdAt: this.convertTimestamp(stage.bonusRound.createdAt),
                updatedAt: this.convertTimestamp(stage.bonusRound.updatedAt)
              }
            : stage.bonusRound
        };
      });

      const gameSession: Omit<GameSession, 'id'> = {
        gameCode,
        teacherId,
        teacherName: teacherProfile.displayName,
        questionSetId: sessionData.questionSetId,
        questionSetName: questionSet.name,
        maxPlayers: sessionData.settings.maxPlayers,
        allowLateJoin: sessionData.settings.allowLateJoin,
        autoAdvance: sessionData.settings.autoAdvance,
        empiricalStudyEnabled: sessionData.settings.empiricalStudyEnabled ?? false,
        players: [],
        currentQuestionIndex: 0,
        currentStageIndex: 0,
        stages,
        questions,
        gameState: 'lobby',
        isActive: false,
        status: 'waiting',
        createdAt: new Date(),
        updatedAt: new Date(),
        currentPhaseStartTime: null,
        currentPhaseEndTime: null,
        currentPhaseDurationSeconds: null,
        currentAnswerStats: null
      };

      const docData = this.deepCleanUndefined({
        ...gameSession,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      }) as Record<string, unknown>;

      const docRef = await addDoc(collection(db, 'gameSessions'), docData);

      console.log('✅ Game session created with ID:', docRef.id, 'and code:', gameCode);
      return { ...gameSession, id: docRef.id };
    } catch (error: unknown) {

      const errorMessage = error instanceof Error ? error.message : String(error);

      console.error('❌ Failed to create game session:', errorMessage);

      throw new Error(`Failed to create game session: ${errorMessage}`);

    }
  }

  async getGameSession(gameCode: string): Promise<GameSession | null> {
    try {
      const q = query(
        collection(db, 'gameSessions'),
        where('gameCode', '==', gameCode)
      );
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        return null;
      }

      const doc = querySnapshot.docs[0];
      const data = doc.data();
      const gameSession: GameSession = {
        id: doc.id,
        gameCode: data.gameCode,
        teacherId: data.teacherId,
        teacherName: data.teacherName,
        questionSetId: data.questionSetId,
        questionSetName: data.questionSetName,
        maxPlayers: data.maxPlayers,
        allowLateJoin: data.allowLateJoin,
        autoAdvance: data.autoAdvance,
        empiricalStudyEnabled: data.empiricalStudyEnabled ?? false,
        players: Array.isArray(data.players)
          ? data.players.map((player: import('@/types').Player) => ({
              ...player,
              authUid: player?.authUid || undefined
            }))
          : [],
        currentQuestionIndex: data.currentQuestionIndex ?? 0,
        currentStageIndex: data.currentStageIndex ?? 0,
        stages: data.stages || [],
        questions: data.questions || [],
        gameState: data.gameState ?? 'lobby',
        isActive: data.isActive ?? false,
        status: data.status ?? 'waiting',
        startedAt: data.startedAt ? this.convertTimestamp(data.startedAt) : undefined,
        completedAt: data.completedAt ? this.convertTimestamp(data.completedAt) : undefined,
        createdAt: this.convertTimestamp(data.createdAt),
        updatedAt: this.convertTimestamp(data.updatedAt),
        currentQuestionStartTime: data.currentQuestionStartTime ? this.convertTimestamp(data.currentQuestionStartTime) : undefined,
        currentPhaseStartTime: data.currentPhaseStartTime ? this.convertTimestamp(data.currentPhaseStartTime) : undefined,
        currentPhaseEndTime: data.currentPhaseEndTime ? this.convertTimestamp(data.currentPhaseEndTime) : undefined,
        currentPhaseDurationSeconds: data.currentPhaseDurationSeconds ?? undefined,
        currentAnswerStats: data.currentAnswerStats ?? undefined
      };

      return gameSession;
    } catch (error: unknown) {
      const err = error as { code?: string; message?: string };
      const errorMessage = err.message || String(error);
      console.error('❌ Failed to get game session:', errorMessage);

      // Provide specific guidance for permission errors
      if (err.code === 'permission-denied' || errorMessage.includes('Missing or insufficient permissions')) {
        const helpMessage = `Permission denied accessing game sessions. This usually means Firebase security rules need to be updated.

🔧 To fix this:
1. Go to Firebase Console → Firestore Database → Rules
2. Update the security rules to include the gameSessions collection
3. See FIREBASE_SETUP.md for the complete security rules

Current error: ${errorMessage}`;

        console.error('🚨 PERMISSION ERROR:', helpMessage);
        throw new Error(`Failed to get game session: ${errorMessage}`);
      }

      throw new Error(`Failed to get game session: ${errorMessage}`);
    }
  }

  async getGameSessionById(sessionId: string): Promise<GameSession | null> {
    try {
      const docRef = doc(db, 'gameSessions', sessionId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      const data = docSnap.data();
      const players = data.players || [];
      // Overlay playerPositions map for contention-free position sync
      const playerPositions = data.playerPositions as Record<string, { x: number; y: number }> | undefined;
      if (playerPositions && typeof playerPositions === 'object') {
        for (const player of players) {
          const pos = playerPositions[player?.id];
          if (pos && typeof pos.x === 'number' && typeof pos.y === 'number') {
            player.position = { x: pos.x, y: pos.y };
          }
        }
      }
      const gameSession: GameSession = {
        id: docSnap.id,
        gameCode: data.gameCode,
        teacherId: data.teacherId,
        teacherName: data.teacherName,
        questionSetId: data.questionSetId,
        questionSetName: data.questionSetName,
        maxPlayers: data.maxPlayers,
        allowLateJoin: data.allowLateJoin,
        autoAdvance: data.autoAdvance,
        empiricalStudyEnabled: data.empiricalStudyEnabled ?? false,
        players,
        currentQuestionIndex: data.currentQuestionIndex ?? 0,
        currentStageIndex: data.currentStageIndex ?? 0,
        stages: data.stages || [],
        questions: data.questions || [],
        gameState: data.gameState ?? 'lobby',
        isActive: data.isActive ?? false,
        status: data.status ?? 'waiting',
        startedAt: data.startedAt ? this.convertTimestamp(data.startedAt) : undefined,
        completedAt: data.completedAt ? this.convertTimestamp(data.completedAt) : undefined,
        createdAt: this.convertTimestamp(data.createdAt),
        updatedAt: this.convertTimestamp(data.updatedAt),
        currentQuestionStartTime: data.currentQuestionStartTime ? this.convertTimestamp(data.currentQuestionStartTime) : undefined,
        currentPhaseStartTime: data.currentPhaseStartTime ? this.convertTimestamp(data.currentPhaseStartTime) : undefined,
        currentPhaseEndTime: data.currentPhaseEndTime ? this.convertTimestamp(data.currentPhaseEndTime) : undefined,
        currentPhaseDurationSeconds: data.currentPhaseDurationSeconds ?? undefined,
        currentAnswerStats: data.currentAnswerStats ?? undefined
      };

      return gameSession;
    } catch (error: unknown) {

      const errorMessage = error instanceof Error ? error.message : String(error);

      console.error('❌ Failed to get game session by ID:', errorMessage);

      throw new Error(`Failed to get game session by ID: ${errorMessage}`);

    }
  }

  async getTeacherGameSessions(teacherId: string): Promise<GameSession[]> {
    try {
      console.log('🎮 Fetching game sessions for teacher:', teacherId);
      const q = query(
        collection(db, 'gameSessions'),
        where('teacherId', '==', teacherId)
      );
      const querySnapshot = await getDocs(q);

      const gameSessions: GameSession[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        gameSessions.push({
          id: doc.id,
          gameCode: data.gameCode,
          teacherId: data.teacherId,
          teacherName: data.teacherName,
          questionSetId: data.questionSetId,
          questionSetName: data.questionSetName,
          maxPlayers: data.maxPlayers,
          allowLateJoin: data.allowLateJoin,
          autoAdvance: data.autoAdvance,
          empiricalStudyEnabled: data.empiricalStudyEnabled ?? false,
          players: data.players || [],
          currentQuestionIndex: data.currentQuestionIndex ?? 0,
          currentStageIndex: data.currentStageIndex ?? 0,
          stages: data.stages || [],
          questions: data.questions || [],
          gameState: data.gameState ?? 'lobby',
          isActive: data.isActive ?? false,
          status: data.status ?? 'waiting',
          startedAt: data.startedAt ? this.convertTimestamp(data.startedAt) : undefined,
          completedAt: data.completedAt ? this.convertTimestamp(data.completedAt) : undefined,
          createdAt: this.convertTimestamp(data.createdAt),
          updatedAt: this.convertTimestamp(data.updatedAt),
          currentQuestionStartTime: data.currentQuestionStartTime ? this.convertTimestamp(data.currentQuestionStartTime) : undefined,
          currentPhaseStartTime: data.currentPhaseStartTime ? this.convertTimestamp(data.currentPhaseStartTime) : undefined,
          currentPhaseEndTime: data.currentPhaseEndTime ? this.convertTimestamp(data.currentPhaseEndTime) : undefined,
          currentPhaseDurationSeconds: data.currentPhaseDurationSeconds ?? undefined,
          currentAnswerStats: data.currentAnswerStats ?? undefined
        });
      });

      console.log(`✅ Found ${gameSessions.length} game sessions`);
      return gameSessions;
    } catch (error: unknown) {

      const errorMessage = error instanceof Error ? error.message : String(error);

      console.error('❌ Failed to get teacher game sessions:', errorMessage);

      throw new Error(`Failed to get teacher game sessions: ${errorMessage}`);

    }
  }

  async updateGameSession(sessionId: string, updates: Partial<GameSession>): Promise<void> {
    try {
      const docRef = doc(db, 'gameSessions', sessionId);
      const firestoreUpdates = this.buildGameSessionUpdatePayload(updates);

      await updateDoc(docRef, firestoreUpdates);
    } catch (error: unknown) {

      const errorMessage = error instanceof Error ? error.message : String(error);

      throw new Error(`Failed to update game session: ${errorMessage}`);

    }
  }

  /**
   * Update ONLY projectiles - matches the isProjectileUpdate() security rule
   * This method ensures only activeNetProjectiles, activeGrenadeProjectiles, and updatedAt are changed
   */
  async updateProjectiles(
    sessionId: string,
    projectiles: {
      activeNetProjectiles?: import('@/types').NetProjectile[];
      activeGrenadeProjectiles?: import('@/types').GrenadeProjectile[];
    }
  ): Promise<void> {
    try {
      const docRef = doc(db, 'gameSessions', sessionId);

      // Build update object with ONLY projectile fields
      const update: Record<string, unknown> = {
        updatedAt: serverTimestamp()
      };

      if (projectiles.activeNetProjectiles !== undefined) {
        update.activeNetProjectiles = projectiles.activeNetProjectiles;
      }

      if (projectiles.activeGrenadeProjectiles !== undefined) {
        update.activeGrenadeProjectiles = projectiles.activeGrenadeProjectiles;
      }

      await updateDoc(docRef, update);
      console.log('✅ Projectiles updated successfully');
    } catch (error: unknown) {

      const errorMessage = error instanceof Error ? error.message : String(error);

      console.error('❌ Failed to update projectiles:', errorMessage);

      throw new Error(`Failed to update projectiles: ${errorMessage}`);

    }
  }

  async deleteGameSession(sessionId: string): Promise<void> {
    try {
      console.log('🗑️ Deleting game session:', sessionId);
      const docRef = doc(db, 'gameSessions', sessionId);
      await deleteDoc(docRef);
      console.log('✅ Game session deleted successfully');
    } catch (error: unknown) {

      const errorMessage = error instanceof Error ? error.message : String(error);

      console.error('❌ Failed to delete game session:', errorMessage);

      throw new Error(`Failed to delete game session: ${errorMessage}`);

    }
  }

  async joinGameSession(sessionId: string, joinData: SessionJoinData): Promise<GameSession> {
    try {
      console.log('🎮 Player joining game session:', sessionId);

      // Get the current session
      const session = await this.getGameSessionById(sessionId);
      if (!session) {
        throw new Error('Game session not found');
      }

      // Check if game allows late join or is in waiting state
      if (session.status !== 'waiting' && !session.allowLateJoin) {
        throw new Error('Game has already started and does not allow late joining');
      }

      // Check if game is full
      if (session.players.length >= session.maxPlayers) {
        throw new Error('Game is full');
      }

      // Check if player name is already taken
      const existingPlayer = session.players.find(p => p.name === joinData.playerName);
      if (existingPlayer) {
        throw new Error('Player name is already taken');
      }

      // Create new player
      const authUid = joinData.authUid || auth.currentUser?.uid || null;

      const newPlayer = {
        id: `player_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        name: joinData.playerName,
        score: 0,
        isReady: false,
        position: getArenaPlayerStartPosition(), // Start in the center of the starting box
        items: [],
        roundScores: [],
        currentRoundScore: 0,
        characterIndex: joinData.characterIndex,
        authUid: authUid || undefined
      };

      // Add player to session
      const updatedPlayers = [...session.players, newPlayer];

      await this.applyGameSessionUpdate(sessionId, {
        players: updatedPlayers
      });

      console.log('✅ Player joined game session successfully');
      return { ...session, players: updatedPlayers };
    } catch (error: unknown) {

      const errorMessage = error instanceof Error ? error.message : String(error);

      console.error('❌ Failed to join game session:', errorMessage);

      throw new Error(`Failed to join game session: ${errorMessage}`);

    }
  }

  async leaveGameSession(sessionId: string, playerId: string): Promise<void> {
    try {
      console.log('🎮 Player leaving game session:', sessionId, playerId);

      // Get the current session
      const session = await this.getGameSessionById(sessionId);
      if (!session) {
        throw new Error('Game session not found');
      }

      // Remove player from session
      const updatedPlayers = session.players.filter(p => p.id !== playerId);

      await this.applyGameSessionUpdate(sessionId, {
        players: updatedPlayers
      });

      console.log('✅ Player left game session successfully');
    } catch (error: unknown) {

      const errorMessage = error instanceof Error ? error.message : String(error);

      console.error('❌ Failed to leave game session:', errorMessage);

      throw new Error(`Failed to leave game session: ${errorMessage}`);

    }
  }

  async executeSessionControl(sessionId: string, action: SessionControlAction): Promise<void> {
    try {
      const session = await this.getGameSessionById(sessionId);
      if (!session) {
        throw new Error('Game session not found');
      }

      const now = new Date();
      const currentStage = this.getStage(session);
      const currentQuestion = currentStage?.type === 'question' ? currentStage.question : undefined;
      const durations = this.getQuestionDurations(currentQuestion);

      let updates: Partial<GameSession> = {};

      switch (action.type) {
        case 'start_game': {
          if (session.stages.length === 0) {
            throw new Error('Cannot start game without any stages configured');
          }

          // Start with countdown phase instead of jumping directly to first question
          updates = {
            gameState: 'countdown',
            status: 'active',
            isActive: true,
            startedAt: now,
            currentPhaseStartTime: now,
            currentPhaseEndTime: new Date(now.getTime() + 3000), // 3 seconds for countdown
            currentPhaseDurationSeconds: 3
          };
          break;
        }

        case 'pause_game':
          updates = {
            status: 'paused',
            isActive: false
          };
          break;

        case 'resume_game':
          updates = {
            status: 'active',
            isActive: true,
            currentPhaseStartTime: now,
            currentPhaseEndTime: session.currentPhaseEndTime ?? null
          };
          break;

        case 'advance_phase':
          if (session.gameState === 'countdown') {
            // Transition from countdown to first question
            const firstStageUpdates = this.prepareStageTransition(session, 0, now);
            updates = {
              ...firstStageUpdates,
              status: 'active',
              isActive: true
            };
          } else if (session.gameState === 'question') {
            updates = {
              gameState: 'running',
              status: 'active',
              isActive: true,
              currentPhaseStartTime: now,
              currentPhaseEndTime: new Date(now.getTime() + durations.running * 1000),
              currentPhaseDurationSeconds: durations.running
            };
        } else if (session.gameState === 'running') {
          // Calculate scores for all players when transitioning to scoring phase
          const updatedPlayers = this.calculateRoundScores(session);

          updates = {
            gameState: 'scoring',
            status: 'active',
            isActive: true,
            currentPhaseStartTime: now,
            currentPhaseEndTime: null,
            currentPhaseDurationSeconds: null,
            players: updatedPlayers  // Update players with calculated scores
          };
          } else if (session.gameState === 'bonus') {
            updates = {
              ...this.prepareStageTransition(session, session.currentStageIndex + 1, now)
            };
          }
          break;

        case 'proceed':
          updates = {
            ...this.prepareStageTransition(session, session.currentStageIndex + 1, now)
          };
          break;

        case 'show_leaderboard':
          if (session.gameState !== 'scoring') {
            throw new Error('Show leaderboard action is only valid during scoring phase');
          }
          updates = {
            gameState: 'leaderboard',
            currentPhaseStartTime: now,
            currentPhaseEndTime: null,
            currentPhaseDurationSeconds: null
          };
          break;

        case 'skip_bonus':
          if (session.gameState !== 'bonus') {
            throw new Error('Skip bonus action is only valid during bonus rounds');
          }
          updates = {
            ...this.prepareStageTransition(session, session.currentStageIndex + 1, now)
          };
          break;

        case 'next_question': {
          const nextQuestionStageIndex = session.stages.findIndex((stage, index) => index > session.currentStageIndex && stage.type === 'question');
          const targetIndex = nextQuestionStageIndex === -1 ? session.stages.length : nextQuestionStageIndex;
          updates = {
            ...this.prepareStageTransition(session, targetIndex, now)
          };
          break;
        }

        case 'end_game':
          updates = {
            gameState: 'finished',
            status: 'completed',
            isActive: false,
            completedAt: now,
            currentPhaseStartTime: null,
            currentPhaseEndTime: null,
            currentPhaseDurationSeconds: null,
            currentAnswerStats: null
          };
          break;

        case 'kick_player':
          if (!action.playerId) {
            throw new Error('Player ID required for kick action');
          }
          updates = {
            players: session.players.filter(p => p.id !== action.playerId)
          };
          break;

        default:
          throw new Error(`Unknown session control action: ${action.type}`);
      }

      await this.applyGameSessionUpdate(sessionId, updates);
    } catch (error: unknown) {

      const errorMessage = error instanceof Error ? error.message : String(error);

      console.error('❌ Failed to execute session control:', errorMessage);

      throw new Error(`Failed to execute session control: ${errorMessage}`);

    }
  }

  async getSessionStats(sessionId: string): Promise<SessionStats> {
    try {
      const session = await this.getGameSessionById(sessionId);
      if (!session) {
        throw new Error('Game session not found');
      }

      const readyCount = session.players.filter(p => p.isReady).length;

      const stats: SessionStats = {
        totalPlayers: session.players.length,
        activeConnections: readyCount,
        currentQuestion: session.currentQuestionIndex + 1,
        totalQuestions: session.questions.length,
        gameProgress: session.questions.length > 0 ? Math.round((session.currentQuestionIndex / session.questions.length) * 100) : 0,
        questionsRemaining: Math.max(0, session.questions.length - session.currentQuestionIndex - 1),
        readyPlayers: readyCount,
        answerDistribution: session.currentAnswerStats ? [...session.currentAnswerStats] : undefined
      };

      // Calculate average response time if we have score data
      const totalResponseTimes = session.players
        .flatMap(p => p.roundScores || [])
        .filter(score => typeof score === 'number' && score > 0);

      if (totalResponseTimes.length > 0) {
        stats.averageResponseTime = totalResponseTimes.reduce((sum, time) => sum + time, 0) / totalResponseTimes.length;
      }

      return stats;
    } catch (error: unknown) {

      const errorMessage = error instanceof Error ? error.message : String(error);

      console.error('❌ Failed to get session stats:', errorMessage);

      throw new Error(`Failed to get session stats: ${errorMessage}`);

    }
  }

  // ==================== AI GAME SESSION METHODS ====================

  async createAiGameSession(
    aiGameData: import('@/types').AiGameCreationData,
    teacherId: string
  ): Promise<import('@/types').AiGameSession> {
    try {
      console.log('🤖 Creating AI game session for teacher:', teacherId);

      // Get question set details
      const questionSet = await this.getQuestionSet(aiGameData.questionSetId);
      if (!questionSet) {
        throw new Error('Question set not found');
      }

      // Get teacher profile
      let teacherProfile = await this.getTeacherProfile(teacherId);
      if (!teacherProfile) {
        const currentUser = await this.getCurrentUser();
        if (currentUser && currentUser.uid === teacherId) {
          const profileData = {
            email: currentUser.email,
            password: '',
            displayName: currentUser.displayName || 'Teacher'
          };
          teacherProfile = await this.createTeacherProfile(profileData, teacherId);
        } else {
          throw new Error('Teacher profile not found');
        }
      }

      // Generate unique game code
      const gameCode = await this.ensureUniqueGameCode();
      console.log('🎯 Generated AI game code:', gameCode);

      const aiGameSession: Omit<import('@/types').AiGameSession, 'id'> = {
        gameCode,
        teacherId,
        teacherName: teacherProfile.displayName,
        questionSetId: questionSet.id,
        questionSetName: questionSet.name,
        questionSetSnapshot: questionSet,
        allowStudentChoice: aiGameData.allowStudentChoice,
        fixedAiPlayerCount: aiGameData.fixedAiPlayerCount,
        endDate: aiGameData.endDate,
        status: 'active',
        createdAt: new Date(),
        updatedAt: new Date(),
        totalPlays: 0,
        activePlayers: []
      };

      // Save to Firebase
      const sessionsRef = collection(db, 'aiGameSessions');
      const docRef = await addDoc(sessionsRef, {
        ...this.sanitizeForFirestore(aiGameSession),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      console.log('✅ AI game session created with ID:', docRef.id);

      return {
        ...aiGameSession,
        id: docRef.id
      };
    } catch (error: unknown) {

      const errorMessage = error instanceof Error ? error.message : String(error);

      console.error('❌ Failed to create AI game session:', errorMessage);

      throw new Error(`Failed to create AI game session: ${errorMessage}`);

    }
  }

  async getAiGameSession(gameCode: string, trackPlay: boolean = false): Promise<import('@/types').AiGameSession | null> {
    try {
      const sessionsRef = collection(db, 'aiGameSessions');
      const q = query(sessionsRef, where('gameCode', '==', gameCode));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        return null;
      }

      const doc = querySnapshot.docs[0];
      const data = doc.data();
      const questionSetSnapshot = this.mapQuestionSetSnapshot(data.questionSetSnapshot, {
        questionSetId: data.questionSetId,
        questionSetName: data.questionSetName,
        teacherId: data.teacherId,
        createdAt: data.createdAt,
        updatedAt: data.updatedAt
      });

      const aiGameSession: import('@/types').AiGameSession = {
        id: doc.id,
        gameCode: data.gameCode,
        teacherId: data.teacherId,
        teacherName: data.teacherName,
        questionSetId: data.questionSetId,
        questionSetName: data.questionSetName,
        questionSetSnapshot,
        allowStudentChoice: data.allowStudentChoice ?? true,
        fixedAiPlayerCount: data.fixedAiPlayerCount,
        endDate: this.convertTimestamp(data.endDate),
        status: data.status ?? 'active',
        createdAt: this.convertTimestamp(data.createdAt),
        updatedAt: this.convertTimestamp(data.updatedAt),
        totalPlays: data.totalPlays ?? 0,
        activePlayers: data.activePlayers ?? []
      };

      // Check if session has expired
      if (aiGameSession.status === 'active' && new Date() > aiGameSession.endDate) {
        aiGameSession.status = 'expired';
        // Update in database
        await updateDoc(doc.ref, { status: 'expired', updatedAt: serverTimestamp() });
      }

      // Track play count if requested (when a student successfully enters the code)
      if (trackPlay && aiGameSession.status === 'active') {
        await this.incrementAiGamePlayCount(doc.id);
        aiGameSession.totalPlays = (aiGameSession.totalPlays || 0) + 1;
      }

      return aiGameSession;
    } catch (error: unknown) {

      const errorMessage = error instanceof Error ? error.message : String(error);

      console.error('❌ Failed to get AI game session:', errorMessage);

      throw new Error(`Failed to get AI game session: ${errorMessage}`);

    }
  }

  async getTeacherAiGameSessions(teacherId: string): Promise<import('@/types').AiGameSession[]> {
    try {
      console.log('📚 Getting AI game sessions for teacher:', teacherId);

      const sessionsRef = collection(db, 'aiGameSessions');
      const q = query(
        sessionsRef,
        where('teacherId', '==', teacherId),
        where('status', 'in', ['active', 'expired'])
      );
      const querySnapshot = await getDocs(q);

      const sessions: import('@/types').AiGameSession[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const questionSetSnapshot = this.mapQuestionSetSnapshot(data.questionSetSnapshot, {
          questionSetId: data.questionSetId,
          questionSetName: data.questionSetName,
          teacherId: data.teacherId,
          createdAt: data.createdAt,
          updatedAt: data.updatedAt
        });

        sessions.push({
          id: doc.id,
          gameCode: data.gameCode,
          teacherId: data.teacherId,
          teacherName: data.teacherName,
          questionSetId: data.questionSetId,
          questionSetName: data.questionSetName,
          questionSetSnapshot,
          allowStudentChoice: data.allowStudentChoice ?? true,
          fixedAiPlayerCount: data.fixedAiPlayerCount,
          endDate: this.convertTimestamp(data.endDate),
          status: data.status ?? 'active',
          createdAt: this.convertTimestamp(data.createdAt),
          updatedAt: this.convertTimestamp(data.updatedAt),
          totalPlays: data.totalPlays ?? 0,
          activePlayers: data.activePlayers ?? []
        });
      });

      console.log(`✅ Found ${sessions.length} AI game sessions`);
      return sessions;
    } catch (error: unknown) {

      const errorMessage = error instanceof Error ? error.message : String(error);

      console.error('❌ Failed to get teacher AI game sessions:', errorMessage);

      throw new Error(`Failed to get teacher AI game sessions: ${errorMessage}`);

    }
  }

  async incrementAiGamePlayCount(sessionId: string): Promise<void> {
    try {
      const sessionRef = doc(db, 'aiGameSessions', sessionId);
      const sessionSnap = await getDoc(sessionRef);

      if (!sessionSnap.exists()) {
        console.warn('⚠️ AI game session not found, skipping play count increment');
        return;
      }

      const currentCount = sessionSnap.data()?.totalPlays ?? 0;
      await updateDoc(sessionRef, {
        totalPlays: currentCount + 1,
        updatedAt: serverTimestamp()
      });
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('❌ Failed to increment AI game play count:', errorMessage);
      // Don't throw error - this is a non-critical tracking operation
    }
  }

  async cancelAiGameSession(sessionId: string): Promise<void> {
    try {
      console.log('🚫 Cancelling AI game session:', sessionId);
      const sessionRef = doc(db, 'aiGameSessions', sessionId);
      await updateDoc(sessionRef, {
        status: 'cancelled',
        updatedAt: serverTimestamp()
      });
      console.log('✅ AI game session cancelled');
    } catch (error: unknown) {

      const errorMessage = error instanceof Error ? error.message : String(error);

      console.error('❌ Failed to cancel AI game session:', errorMessage);

      throw new Error(`Failed to cancel AI game session: ${errorMessage}`);

    }
  }

  // Debug helper method
  private async listAllTeacherProfiles(): Promise<string[]> {
    try {
      const q = query(collection(db, 'teacherProfiles'));
      const querySnapshot = await getDocs(q);
      const profiles: string[] = [];
      querySnapshot.forEach((doc) => {
        profiles.push(`${doc.id}: ${doc.data().email || 'no-email'}`);
      });
      return profiles;
    } catch (error) {
      console.error('Failed to list teacher profiles:', error);
      return ['Error fetching profiles'];
    }
  }
}
